import React, { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { useActionHook, usePrompt } from "hooks";
import { useDigestSettingsSelector, useNewsFeedActions, useUserSelector } from "store";
import type {
  ArticleRow,
  ArticlesFilters,
  ArticlesPageParams,
  dailyBrief,
  // DealsCoverage,
  DigestRow,
  DigestSettingsForm
} from "store/types/news";

import { RoleName } from "../../../constants";
import { BriefingDialog } from "../components/BriefingDialog";
// import { CoverageManagerDialog } from "../components/CoverageManagerDialog";
// import { DealCoverageDialog } from "../components/DealCoverageDialog";
import { DigestHighlightsDialog } from "../components/DigestHighlightsDialog";
import { DigestSettingsDialog } from "../components/DigestSettingsDialog";
import { type FeedConfig, useFeedConfigSettings } from "../hooks/use-feed-config-settings";

const ARTICLES_PER_PAGE = 20;

function buildArticlesParams(filters: Filters, page: number): ArticlesPageParams {
  const params: ArticlesPageParams = { page, limit: ARTICLES_PER_PAGE };
  const filterMap: ArticlesFilters = {
    type: filters.type,
    topic: filters.topic,
    source: filters.source,
    deal: filters.deal,
    search: filters.search
  };
  for (const [key, value] of Object.entries(filterMap)) {
    if (value) {
      (params as unknown as Record<string, unknown>)[key] = value;
    }
  }
  return params;
}

export type Filters = { type: string; topic: string; source: string; deal: string; search: string };

export type FeedConfigBuckets<T> = {
  system: T[];
  user: T[];
};

const DEFAULT_FILTERS: Filters = { type: "", topic: "", source: "", deal: "", search: "" };

interface NewsFeedContextValue {
  // user-editable feed config (Configure feed dialog)
  feedConfig: FeedConfig;
  userHasAccessEdit: boolean;

  // data
  articles: ArticleRow[];
  articlesHasMore: boolean;
  articlesLoading: boolean;
  loadMoreArticles: () => void;
  digestHistory: DigestRow[];
  digestSettings: DigestSettingsForm;
  briefing: dailyBrief | null;

  // coverage data
  // dealsCoverage: DealsCoverage[];

  // page ui state (non-dialog)
  feedTab: FeedTab;
  filters: Filters;

  // actions — page ui
  setFeedTab: (tab: FeedTab) => void;
  setFilters: (updater: Filters | ((prev: Filters) => Filters)) => void;
  resetFilters: () => void;

  // actions — coverage mutations
  // addCoverageKeyword: (dealId: string, keyword: string) => void;
  // removeCoverageKeyword: (dealId: string, keyword: string) => void;
  // toggleDealTracked: (dealId: string) => void;

  // actions — prompts
  openConfigureFeed: () => Promise<void>;
  // openDealCoverageDialog: (dealId: string) => Promise<void>;
  // openCoverageManager: () => Promise<void>;
  openDigestSettings: () => Promise<void>;
  openBriefing: () => void;
  openDigestHighlights: (row: DigestRow) => void;
}

const NewsFeedContext = createContext<NewsFeedContextValue | null>(null);

interface ProviderProps {
  initialDigest?: DigestRow[];
  initialDigestSettings?: DigestSettingsForm;
}

export enum FeedTab {
  LiveFeed = "Live Feed",
  PastDigests = "Past Digests"
}

export const NewsFeedProvider: React.FC<PropsWithChildren<ProviderProps>> = ({ children }) => {
  const openPrompt = usePrompt();

  const {
    getArticlesAction,
    UpdateDigestSettingsAction,
    UpdateNewsServiceAction,
    GetDailyBriefAction,
    GetPastDigestsAction
  } = useNewsFeedActions();
  const { role } = useUserSelector();
  const digestSettings = useDigestSettingsSelector();
  const { feedConfig, openConfigureFeed } = useFeedConfigSettings();

  const userHasAccessEdit = useMemo(
    () => role?.name === RoleName.Admin || role?.name === RoleName.Analyst,
    [role?.name]
  );

  //past digests history
  const [digestHistory, setDigestHistory] = useState<DigestRow[]>([]);

  //live feed ai briefing
  const [briefing, setBriefing] = useState<dailyBrief | null>(null);

  //news deals coverage
  // const [dealsCoverage, setDealsCoverage] = useState<DealsCoverage[]>(SEED_DEALS_COVERAGE);

  //current tab
  const [feedTab, setFeedTab] = useState<FeedTab>(FeedTab.LiveFeed);

  //filters
  const [filters, setFiltersState] = useState<Filters>(DEFAULT_FILTERS);

  const { action: getPastDigests } = useActionHook(GetPastDigestsAction, {
    onSuccess: (data) => {
      setDigestHistory(data);
    }
  });

  const { action: getDailyBrief } = useActionHook(GetDailyBriefAction, {
    onSuccess: (data) => {
      setBriefing(data);
    }
  });

  const setFilters = useCallback((updater: Filters | ((prev: Filters) => Filters)) => {
    setFiltersState((prev) => (typeof updater === "function" ? updater(prev) : updater));
  }, []);

  const resetFilters = useCallback(() => setFiltersState(DEFAULT_FILTERS), []);

  //live feed articles — server-driven, paginated
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [articlesPage, setArticlesPage] = useState(0);
  const [articlesHasMore, setArticlesHasMore] = useState(false);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const fetchTokenRef = React.useRef(0);

  const fetchArticlesPage = useCallback(
    async (page: number, queryFilters: Filters) => {
      const token = ++fetchTokenRef.current;
      setArticlesLoading(true);
      try {
        const res = await getArticlesAction(buildArticlesParams(queryFilters, page));
        if (token !== fetchTokenRef.current) return;
        setArticles((prev) => (page === 1 ? res.articles : [...prev, ...res.articles]));
        setArticlesPage(res.page);
        setArticlesHasMore(res.has_more);
      } finally {
        if (token === fetchTokenRef.current) {
          setArticlesLoading(false);
        }
      }
    },
    [getArticlesAction]
  );

  useEffect(() => {
    if (feedTab !== FeedTab.LiveFeed) return;
    fetchArticlesPage(1, filters);
  }, [feedTab, filters, fetchArticlesPage]);

  const loadMoreArticles = useCallback(() => {
    if (articlesLoading || !articlesHasMore) return;
    fetchArticlesPage(articlesPage + 1, filters);
  }, [articlesLoading, articlesHasMore, articlesPage, filters, fetchArticlesPage]);

  // const addCoverageKeyword = useCallback((dealId: string, keyword: string) => {
  //   setDealsCoverage((prev) =>
  //     prev.map((deal) =>
  //       deal.id === dealId && !deal.keywords.includes(keyword)
  //         ? { ...deal, keywords: [...deal.keywords, keyword] }
  //         : deal
  //     )
  //   );
  // }, []);

  // const removeCoverageKeyword = useCallback((dealId: string, keyword: string) => {
  //   setDealsCoverage((prev) =>
  //     prev.map((deal) =>
  //       deal.id === dealId ? { ...deal, keywords: deal.keywords.filter((k) => k !== keyword) } : deal
  //     )
  //   );
  // }, []);

  // const toggleDealTracked = useCallback((dealId: string) => {
  //   setDealsCoverage((prev) => prev.map((deal) => (deal.id === dealId ? { ...deal, tracked: !deal.tracked } : deal)));
  // }, []);

  // const openDealCoverageDialog = useCallback(
  //   async (dealId: string) => {
  //     await openPrompt({
  //       componentType: "modal",
  //       Component: DealCoverageDialog,
  //       props: {
  //         dealId,
  //         dealsCoverage,
  //         setDealsCoverage
  //       }
  //     });
  //   },
  //   [openPrompt, dealsCoverage]
  // );

  // const openCoverageManager = useCallback(async () => {
  //   await openPrompt({
  //     componentType: "modal",
  //     Component: CoverageManagerDialog,
  //     props: {
  //       dealsCoverage,
  //       setDealsCoverage,
  //       onOpenDealCoverage: openDealCoverageDialog,
  //       userHasAccessEdit
  //     }
  //   });
  // }, [openPrompt, dealsCoverage, openDealCoverageDialog]);

  const openDigestSettings = useCallback(async () => {
    const settings = await openPrompt({
      componentType: "modal",
      Component: DigestSettingsDialog,
      props: { settings: digestSettings }
    });
    if (!settings) return;
    await UpdateDigestSettingsAction(settings);
  }, [openPrompt, digestSettings, UpdateDigestSettingsAction]);

  const openBriefing = useCallback(() => {
    openPrompt({
      componentType: "modal",
      Component: BriefingDialog,
      props: { briefing }
    });
  }, [openPrompt, briefing]);

  const openDigestHighlights = useCallback(
    (row: DigestRow) => {
      openPrompt({
        componentType: "modal",
        Component: DigestHighlightsDialog,
        props: { row, deliveryTime: digestSettings.deliveryTime }
      });
    },
    [openPrompt, digestSettings.deliveryTime]
  );

  useEffect(() => {
    getPastDigests();
    getDailyBrief();
    UpdateNewsServiceAction();
  }, []);

  const value = useMemo<NewsFeedContextValue>(
    () => ({
      feedConfig,
      userHasAccessEdit,
      articles,
      articlesHasMore,
      articlesLoading,
      loadMoreArticles,
      digestHistory,
      digestSettings,
      briefing,
      // dealsCoverage,
      feedTab,
      filters,
      setFeedTab,
      setFilters,
      resetFilters,
      // addCoverageKeyword,
      // removeCoverageKeyword,
      // toggleDealTracked,
      openConfigureFeed,
      // openDealCoverageDialog,
      // openCoverageManager,
      openDigestSettings,
      openBriefing,
      openDigestHighlights
    }),
    [
      feedConfig,
      userHasAccessEdit,
      articles,
      articlesHasMore,
      articlesLoading,
      loadMoreArticles,
      digestHistory,
      digestSettings,
      briefing,
      // dealsCoverage,
      feedTab,
      filters,
      setFilters,
      resetFilters,
      openConfigureFeed,
      // openDealCoverageDialog,
      // openCoverageManager,
      openDigestSettings,
      openBriefing,
      openDigestHighlights
    ]
  );

  return <NewsFeedContext.Provider value={value}>{children}</NewsFeedContext.Provider>;
};

export const useNewsFeed = (): NewsFeedContextValue => {
  const ctx = useContext(NewsFeedContext);
  if (!ctx) throw new Error("useNewsFeed must be used within NewsFeedProvider");
  return ctx;
};
